import React from 'react';
import { useIsomorphicLayoutEffect } from 'usehooks-ts';

import { SecondaryButton } from '@components/Button/variants';
import CustomSpinner, { CustomSpinnerColor } from '@components/Spinner/CustomSpinner';
import useComments from '@hooks/useComments';
import useEventTracking from '@hooks/useEventTracking';
import { ClickTarget, ImpressionElement, click, impression } from '@tracking';
import { COMMENTS_PER_PAGE } from '@utils/backend-api/comments';
import { CommentableType } from '@utils/backend-api/comments';

import CommentFormContainer from './Comment/Form/Container';
import CommentsFeed from './Feed';

interface LazyContentProps {
  commentableId: number;
  commentableType: CommentableType;
  commentId?: string;
}

const LazyContent = ({
  commentableId,
  commentableType,
  commentId,
}: LazyContentProps): JSX.Element => {
  const { trackEvent } = useEventTracking();

  const { commentsData, isLoadingComments, fetchNextPage, hasMoreComments, totalComments } =
    useComments(commentableId, commentableType, commentId);

  useIsomorphicLayoutEffect(() => {
    if (!commentId || isLoadingComments) return;

    const deepLinkedComment = document.getElementById(`comment-${commentId}`);
    deepLinkedComment?.scrollIntoView({ behavior: 'smooth' });
    trackEvent(impression({ impressionElement: ImpressionElement.DeepLinkedComment }));
  }, [commentId, isLoadingComments]);

  const remainingCommentsCount = () => {
    const totalLoadedComments = commentsData?.pages.reduce(
      (acc, page) =>
        acc +
        page.comments.length +
        page.comments.reduce((subAcc, comment) => subAcc + comment.child_comments.length, 0),
      0
    );

    const remainingComments = totalComments - totalLoadedComments;
    return Math.min(COMMENTS_PER_PAGE, remainingComments);
  };

  const handleNextPage = () => {
    fetchNextPage();
    trackEvent(click({ clickTarget: ClickTarget.ShowMoreComments }));
  };

  return (
    <>
      {isLoadingComments ? (
        <CustomSpinner
          size={15}
          color={CustomSpinnerColor.PrimaryMagenta}
          style={{ display: 'flex', justifyContent: 'center', marginTop: '56px' }}
        />
      ) : (
        <>
          <CommentFormContainer />
          {commentsData?.pages.map((group, i) => (
            <div className="comment-list" key={i}>
              <CommentsFeed comments={group.comments} commentableType={commentableType} />
            </div>
          ))}
          {hasMoreComments && (
            <SecondaryButton
              as="button"
              size="small"
              onClick={handleNextPage}
              className="more-comments mt-48 w-full bg-white lg:mt-64"
            >
              Show more comments ( {remainingCommentsCount()} )
            </SecondaryButton>
          )}
        </>
      )}
    </>
  );
};

export default LazyContent;
